import type { Notification } from '~/types/notifications'
import { useAuthStore } from './auth'
import type { LikeObjectType } from '~/types/like'
import type { Settings } from '~/types/types'

export interface UserLocation {
  latitude: number
  longitude: number
  type: 'geoip' | 'app'
}

export interface GeoIpUserLocation extends UserLocation {
  type: 'geoip'
  countryCode: string
}

export interface AppUserLocation extends UserLocation {
  type: 'app'
}

interface State {
  likeListModal: {
    isVisible: boolean
    objectId?: number
    objectType?: LikeObjectType
    /** e.g. group id for nested like lists (`group_postings`).*/
    objectParentId?: number
  }
  photoModal: {
    isVisible: boolean
    photos: {
      id: number
      url: string
    }[]
    selectedPhotoId?: number
  }
  tripPhotoModal: {
    isVisible: boolean
    tripId?: number
    selectedPhotoId?: number
  }
  alertModal: {
    isVisible: boolean
    title?: string
    message?: string
  }
  inviteFriendModal: {
    isVisible: boolean
  }
  notifications: Notification[]
  settings: Settings | null
  isLoadedFromApp: boolean
  userLocation?: GeoIpUserLocation | AppUserLocation
}

export const useMainStore = defineStore('main', {
  state: (): State => ({
    likeListModal: {
      isVisible: false,
      objectId: undefined,
      objectType: undefined,
      objectParentId: undefined
    },
    photoModal: {
      isVisible: false,
      photos: [],
      selectedPhotoId: undefined
    },
    tripPhotoModal: {
      isVisible: false,
      tripId: undefined,
      selectedPhotoId: undefined
    },
    alertModal: {
      isVisible: false,
      title: undefined,
      message: undefined
    },
    inviteFriendModal: {
      isVisible: false
    },
    notifications: [],
    settings: {
      distance: 'metric',
      temperature: 'celsius',
      twelveHourClock: false,
      weekStartsOn: 7
    },
    isLoadedFromApp: false,
    userLocation: undefined
  }),

  actions: {
    showAlertModal(message: string, title?: string) {
      this.alertModal.isVisible = true
      this.alertModal.message = message
      this.alertModal.title = title
    },

    showPhotoModal(photos: { id: number; url: string }[], selectedPhotoId?: number) {
      this.photoModal.isVisible = true
      this.photoModal.photos = photos
      this.photoModal.selectedPhotoId = selectedPhotoId
    },

    hidePhotoModal() {
      this.photoModal.isVisible = false
      this.photoModal.photos = []
      this.photoModal.selectedPhotoId = undefined
    },

    showTripPhotoModal(tripId: number, selectedPhotoId?: number) {
      this.tripPhotoModal.isVisible = true
      this.tripPhotoModal.tripId = tripId
      this.tripPhotoModal.selectedPhotoId = selectedPhotoId
    },

    hideTripPhotoModal() {
      this.tripPhotoModal.isVisible = false
      this.tripPhotoModal.tripId = undefined
      this.tripPhotoModal.selectedPhotoId = undefined
    },

    dismissAlertModal() {
      this.alertModal.isVisible = false
      this.alertModal.message = undefined
      this.alertModal.title = undefined
    },

    showLikeListModal(objectType: LikeObjectType, objectId: number, objectParentId?: number) {
      this.likeListModal.isVisible = true
      this.likeListModal.objectId = objectId
      this.likeListModal.objectType = objectType
      this.likeListModal.objectParentId = objectParentId
    },

    hideLikeListModal() {
      this.likeListModal.isVisible = false
      this.likeListModal.objectId = undefined
      this.likeListModal.objectType = undefined
      this.likeListModal.objectParentId = undefined
    },

    showInviteFriendModal() {
      this.inviteFriendModal.isVisible = true
    },

    hideInviteFriendModal() {
      this.inviteFriendModal.isVisible = false
    },

    acceptTos() {
      const authStore = useAuthStore()

      if (authStore.session_data) {
        authStore.session_data.user.accepted_latest_tos = true
      }
    },

    setNotifications(data: Notification[]) {
      this.notifications = data
    },

    updateSettings(settings: any) {
      const authStore = useAuthStore()

      if (!authStore?.session_data.user) {
        return
      }

      if (!settings.weekStartsOn) {
        settings.weekStartsOn = getWeekStartDay()
      }

      this.settings = { ...this.settings, ...JSON.parse(JSON.stringify(settings)) }
    },

    setIsLoadedFromApp(value: boolean) {
      this.isLoadedFromApp = value
    },

    hideScrollbar() {
      if (!document) return
      document.querySelector('body')?.classList.add('no-scrollbar')

      /* 
        On Android, scrolling upwards is sometimes not possible as soon as we hide the body overflow
        Therefore we scroll down by 1px so that the native client thinks there's something to scroll up to
      */
      window.scrollBy(0, 1)
      document.body.style.minHeight = 'calc(100vh + 1px)'
    },

    unhideScrollbar() {
      if (!document) return
      document.querySelector('body')?.classList.remove('no-scrollbar')

      window.scrollBy(0, -1)
      document.body.style.minHeight = '0px'
    }
  }
})
